/**
 * FINAL ACTION GATES
 *
 * Gate validation functions for the final action menu in DocumentModal.
 * These are runtime logic that cannot be expressed in YAML.
 *
 * The gate logic is shared across supplier_invoice, expense, and DIN
 * with minor variations in the "missing supplier" message.
 */

import type { Document, DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import { hasMeaningfulLineItemContent } from '@/components/finance/modals/documentModalHelpers';
import { getDinLineLinkGaps } from '@/lib/finance/expenseAccountMapping';

export interface GateResult {
  pass: boolean;
  message?: string;
}

export interface FinalActionGateContext {
  /** custom_settings.inventory_tracking_enabled — gates the stock-item link requirement. */
  inventoryTrackingEnabled?: boolean;
}

export type FinalActionGateFn = (
  doc: Partial<Document>,
  lineItems: DocumentLineItem[],
  ctx?: FinalActionGateContext,
) => GateResult;

/**
 * Filter to meaningful line items (excludes spacers, headers, comments, subtotals).
 */
function getMeaningfulItems(lineItems: DocumentLineItem[]): DocumentLineItem[] {
  return lineItems.filter((item) => {
    const lineType = item.line_type || 'item';
    if (lineType === 'spacer') return false;
    if (lineType === 'header' || lineType === 'comment') return false;
    if (lineType === 'subtotal') return false;
    return hasMeaningfulLineItemContent(item);
  });
}

/**
 * Shared line-item checks for supplier-facing documents — line presence,
 * GL account, allocation type, and linked-entity requirements. Kept separate
 * from the contact check so employee reimbursement claims (which are
 * contactless by design) can reuse it.
 */
function checkLineItemGates(
  lineItems: DocumentLineItem[],
  docTypeLabel: string,
  ctx?: FinalActionGateContext,
): GateResult {
  const meaningfulItems = getMeaningfulItems(lineItems);

  if (meaningfulItems.length === 0) {
    return {
      pass: false,
      message: `At least one line item is required before completing this ${docTypeLabel}.`,
    };
  }

  const missingGL = meaningfulItems.filter((item) => !item.expense_account_code?.trim());
  if (missingGL.length > 0) {
    const firstDesc = missingGL[0]?.description?.trim() || `Line ${lineItems.indexOf(missingGL[0]) + 1}`;
    return {
      pass: false,
      message:
        missingGL.length === 1
          ? `Line item "${firstDesc}" is missing a GL account. Please allocate a GL account before completing.`
          : `${missingGL.length} line items are missing a GL account. Please allocate GL accounts before completing.`,
    };
  }

  const missingAllocation = meaningfulItems.filter((item) => !item.allocation_type?.trim());
  if (missingAllocation.length > 0) {
    const firstDesc = missingAllocation[0]?.description?.trim() || `Line ${lineItems.indexOf(missingAllocation[0]) + 1}`;
    return {
      pass: false,
      message:
        missingAllocation.length === 1
          ? `Line item "${firstDesc}" is missing an allocation type. Please allocate an allocation type before completing.`
          : `${missingAllocation.length} line items are missing an allocation type. Please allocate allocation types before completing.`,
    };
  }

  // Customer + job is compulsory only on lines marked billable — QBO parity:
  // the Customer column is optional on expense lines unless the line is
  // billed back to a customer:job. A job-allocated but non-billable line may
  // keep or drop its link freely; links are never auto-removed.
  const missingBillableLink = meaningfulItems.filter(
    (item) => getDinLineLinkGaps(item, ctx).missingBillableCustomerJob,
  );
  if (missingBillableLink.length > 0) {
    const firstDesc = missingBillableLink[0]?.description?.trim() || `Line ${lineItems.indexOf(missingBillableLink[0]) + 1}`;
    return {
      pass: false,
      message:
        missingBillableLink.length === 1
          ? `Line item "${firstDesc}" is marked billable but has no customer/job linked. Please link a customer and job, or uncheck Billable.`
          : `${missingBillableLink.length} line items are marked billable but have no customer/job linked. Please link them, or uncheck Billable.`,
    };
  }

  const missingStock = meaningfulItems.filter(
    (item) => getDinLineLinkGaps(item, ctx).missingStockItem,
  );
  if (missingStock.length > 0) {
    const firstDesc = missingStock[0]?.description?.trim() || `Line ${lineItems.indexOf(missingStock[0]) + 1}`;
    return {
      pass: false,
      message:
        missingStock.length === 1
          ? `Line item "${firstDesc}" is allocated to Stock but no inventory item is linked. Please select an inventory item before completing.`
          : `${missingStock.length} line items are allocated to Stock but no inventory item is linked. Please link inventory items before completing.`,
    };
  }

  // Equipment links are always optional — QBO has no equipment dimension.

  return { pass: true };
}

/**
 * Build a shared gate function for supplier-facing documents (supplier_invoice, expense).
 * The only variation is the document type label used in the "missing supplier" message.
 */
function buildSupplierGate(docTypeLabel: string): FinalActionGateFn {
  return (doc: Partial<Document>, lineItems: DocumentLineItem[], ctx?: FinalActionGateContext): GateResult => {
    if (!doc.contact_id) {
      return {
        pass: false,
        message: `A supplier must be selected before completing this ${docTypeLabel}.`,
      };
    }
    return checkLineItemGates(lineItems, docTypeLabel, ctx);
  };
}

/**
 * Named gate functions, keyed by the `gate_type` field in YAML.
 */
export const FINAL_ACTION_GATES: Record<string, FinalActionGateFn> = {
  supplier_invoice: buildSupplierGate('supplier invoice'),
  expense: buildSupplierGate('expense'),
  din: (doc: Partial<Document>, lineItems: DocumentLineItem[], ctx?: FinalActionGateContext): GateResult => {
    // Employee reimbursement claims (mileage, per-diem) are contactless by
    // design — the employee is the payee via assigned_to, not a supplier
    // contact. DINs carrying a reimbursement_route bypass the supplier gate;
    // every other gate (line items, GL, allocation) still applies.
    const isEmployeeReimbursementClaim =
      typeof doc.custom_fields?.reimbursement_route === 'string';
    if (!doc.contact_id && !isEmployeeReimbursementClaim) {
      return {
        pass: false,
        message:
          'A supplier must be selected before completing this DIN. If the supplier does not exist yet, please create it first.',
      };
    }
    return checkLineItemGates(lineItems, 'DIN', ctx);
  },
};

/**
 * Named condition functions for final action menu items, keyed by the
 * `condition_name` field in YAML.
 */
export const FINAL_ACTION_ITEM_CONDITIONS: Record<string, (doc: Partial<Document>, items: DocumentLineItem[]) => boolean> = {
  has_id_and_contact_no_parent: (doc) =>
    Boolean(doc.id) && Boolean(doc.contact_id) && !doc.parent_document_id,
};
